import { useState, type FormEvent } from 'react';
import { Link, Navigate, useLocation, useNavigate } from 'react-router-dom';
import authService from '@wlb/shared/api/services/authService';
import { useTranslation } from '@wlb/shared/i18n/useTranslation';
import { ToastService } from '@wlb/shared/platform/notifier';
import { getApiErrorMessage } from '@wlb/shared/utils/apiErrorHandler';
import type { ApiError } from '@wlb/shared/types';
import { BrandLogo } from '../components/ui/BrandLogo';
import { FormError } from '../components/ui/FormError';
import { PasswordInput } from '../components/ui/PasswordInput';
import { CheckIcon } from '../components/ui/icons';
import { AuthFlowLayout } from '../components/layout/AuthFlowLayout';
import { PasswordCriteria, isPasswordStrong } from '../features/auth/PasswordCriteria';
import type { AuthFlowState } from '../features/auth/authFlowState';
import '../features/auth/auth.css';

export function ResetPassword() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const state = location.state as AuthFlowState | null;
  const email = state?.email ?? '';
  const passwordResetToken = state?.passwordResetToken ?? '';
  const otpCode = state?.otpCode ?? '';

  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  // Errors appear on blur rather than on submit: the submit button stays
  // disabled until the form is valid, so a submit-gated message would never
  // reach the user who needs it.
  const [newTouched, setNewTouched] = useState(false);
  const [confirmTouched, setConfirmTouched] = useState(false);
  const [loading, setLoading] = useState(false);

  // The OTP step is the gate for this screen; ValidatePasswordResetToken only
  // recognises tokens issued for the emailed reset link, not OTP tokens.
  if (!email || !passwordResetToken || !otpCode) {
    return <Navigate to="/forgot-password" replace />;
  }

  const passwordStrong = isPasswordStrong(newPassword);
  // Two empty fields are equal, so the confirm value must be present before
  // this counts as a match — otherwise the tick shows on an untouched form.
  const passwordsMatch = confirmPassword.length > 0 && newPassword === confirmPassword;
  const canSubmit = passwordStrong && passwordsMatch;

  const newPasswordWeak = newTouched && newPassword.length > 0 && !passwordStrong;
  const newPasswordError = newTouched && !newPassword ? t('validation.passwordRequired') : '';
  const confirmPasswordError = !confirmTouched
    ? ''
    : !confirmPassword
      ? t('validation.confirmPasswordRequired')
      : !passwordsMatch
        ? t('validation.passwordsDoNotMatch')
        : '';

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    setNewTouched(true);
    setConfirmTouched(true);
    // The button is disabled in this state; kept as a guard for programmatic
    // submits.
    if (!canSubmit) {
      return;
    }
    setLoading(true);
    try {
      await authService.resetPassword({ email, newPassword, otpCode, passwordResetToken });
      navigate('/password-reset-success', { replace: true });
    } catch (err) {
      ToastService.show({ type: 'error', message: getApiErrorMessage(err as ApiError) });
    } finally {
      setLoading(false);
    }
  };

  return (
    <AuthFlowLayout>
      <div className="auth-flow__card">
        <button className="auth-flow__back" type="button" onClick={() => navigate(-1)}>
          ‹ {t('common.buttons.back')}
        </button>
        <BrandLogo compact className="auth-flow__brand" />
        <h1 className="auth-flow__heading">{t('auth.changePassword.heading')}</h1>
        <p className="auth-flow__subheading">{t('auth.changePassword.subheading')}</p>

        <form className="auth-flow__form" onSubmit={submit} noValidate>
          <label className="auth-flow__field">
            <span>{t('auth.changePassword.newPasswordLabel')}</span>
            <PasswordInput
              className={`auth-flow__input${newPasswordError || newPasswordWeak ? ' auth-flow__input--error' : ''}`}
              value={newPassword}
              onChange={event => setNewPassword(event.target.value)}
              onBlur={() => setNewTouched(true)}
              placeholder={t('auth.changePassword.newPasswordPlaceholder')}
              autoComplete="new-password"
              aria-invalid={Boolean(newPasswordError) || newPasswordWeak}
              aria-describedby={`reset-password-criteria${newPasswordError ? ' reset-password-error' : ''}`}
              autoFocus
            />
            <PasswordCriteria password={newPassword} id="reset-password-criteria" />
            <FormError message={newPasswordError} id="reset-password-error" />
          </label>

          <label className="auth-flow__field">
            <span>{t('auth.changePassword.confirmPasswordLabel')}</span>
            <PasswordInput
              className={`auth-flow__input${confirmPasswordError ? ' auth-flow__input--error' : ''}`}
              value={confirmPassword}
              onChange={event => setConfirmPassword(event.target.value)}
              onBlur={() => setConfirmTouched(true)}
              placeholder={t('auth.changePassword.confirmPasswordPlaceholder')}
              autoComplete="new-password"
              aria-invalid={Boolean(confirmPasswordError)}
              aria-describedby={
                confirmPasswordError
                  ? 'reset-confirm-error'
                  : passwordsMatch
                    ? 'reset-confirm-match'
                    : undefined
              }
            />
            <FormError message={confirmPasswordError} id="reset-confirm-error" />
            {passwordsMatch && (
              <p className="auth-flow__match" id="reset-confirm-match" role="status">
                <CheckIcon size={16} />
                {t('auth.changePassword.passwordsMatch')}
              </p>
            )}
          </label>

          <button
            className={`auth-flow__primary${!canSubmit && !loading ? ' auth-flow__primary--blocked' : ''}`}
            type="submit"
            disabled={loading || !canSubmit}
          >
            {loading ? t('common.loading') : t('auth.changePassword.resetPassword')}
          </button>
        </form>

        <Link to="/login" className="auth-flow__footer">{t('auth.changePassword.cancelBackToSignIn')}</Link>
      </div>
    </AuthFlowLayout>
  );
}
